import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useQuery } from '../hooks/useApi';
import { EmptyState, ErrorState, Spinner } from '../components/States';
import { SkinCard } from '../components/SkinCard';
import { useI18n } from '../i18n';

/**
 * Primeira página: a vitrine curada no painel.
 *
 * Sem destaques configurados, a home não fica vazia — leva para o catálogo,
 * que é o que o visitante veio ver de qualquer forma.
 */
export function HomePage() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const query = useQuery(() => api.featured(), []);
  const pt = lang === 'pt';

  if (query.loading) return <Spinner label={pt ? 'Carregando os destaques…' : 'Loading highlights…'} />;
  if (query.error) return <ErrorState error={query.error} onRetry={query.refetch} />;

  if (!query.data || query.data.items.length === 0) {
    return (
      <EmptyState>
        {pt ? 'Nenhuma skin em destaque ainda. ' : 'No featured skins yet. '}
        {/* Link, e não <a href>: âncora comum recarrega o site inteiro. */}
        <Link to="/all">{pt ? 'Ver o catálogo completo' : 'Browse the full catalog'}</Link>
      </EmptyState>
    );
  }

  return (
    <>
      <div className="home-head">
        <h1>{pt ? 'Mais populares' : 'Most popular'}</h1>
        <p>
          {pt
            ? 'Seleção da casa, com preço final em reais e entrega no seu trade link.'
            : 'Our picks, final price in BRL, delivered to your trade link.'}
        </p>
      </div>

      <div className="grid">
        {query.data.items.map((group) => (
          <SkinCard
            key={group.key}
            group={group}
            onClick={() => {
              // O destaque guarda de onde a skin veio; sem isso não há como
              // montar a rota do catálogo para abrir os desgastes.
              const code = group.variants[0]?.weaponCode;
              const kind = group.variants[0]?.kind;
              const base = code ? `/w/${code}` : kind ? `/k/${kind}` : '/all';
              navigate(`${base}/s/${encodeURIComponent(group.key)}`);
            }}
          />
        ))}
      </div>

      <ConviteCatalogo total={query.data.catalogSize} pt={pt} />
    </>
  );
}

/**
 * Convite para o catálogo, no fim da vitrine.
 *
 * É o único caminho para quem rolou os destaques e não se decidiu — por isso
 * deixou de ser um botão discreto. O número de skins vai junto: "ver o
 * catálogo" é um convite vago, e "quase trinta mil skins" é um motivo.
 *
 * Usa Link, e não <a href>: com âncora comum o navegador recarrega o site
 * inteiro, e o catálogo demora a aparecer numa navegação que deveria ser
 * instantânea.
 */
function ConviteCatalogo({ total, pt }: { total: number; pt: boolean }) {
  return (
    <div className="home-more">
      <Link className="cta-catalogo" to="/all">
        <span className="cta-catalogo-texto">
          <b>{pt ? 'Ver o catálogo completo' : 'Browse the full catalog'}</b>
          {total > 0 && (
            <span>
              {pt ? (
                <>
                  <i>{total.toLocaleString('pt-BR')}</i> skins com preço final em reais
                </>
              ) : (
                <>
                  <i>{total.toLocaleString('en-US')}</i> skins, final price in BRL
                </>
              )}
            </span>
          )}
        </span>
        <span className="cta-catalogo-seta" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h13M12.5 5.5L19 12l-6.5 6.5" />
          </svg>
        </span>
      </Link>
    </div>
  );
}
